/**
 * ProductDetail — orchestrator for `/menu/{store}/{slug}`.
 *
 * Owns data fetching (product, store, specials), derives state (variant
 * selection, applicable special, sale flag), and hands the rest off to
 * ProductGallery + ProductBuyBox.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import { getMenuProduct, getLocation, getSpecials } from "../lib/api";
import { breadcrumbSchema } from "../lib/seo";
import { useCart } from "../lib/cart";
import { discountSticker } from "../lib/promos";

import { productSchema } from "./product/productSchema";
import ProductGallery from "./product/ProductGallery";
import ProductBuyBox from "./product/ProductBuyBox";

export default function ProductDetail() {
    const { storeSlug, productSlug } = useParams();
    const [data, setData] = useState(null);
    const [store, setStore] = useState(null);
    const [specials, setSpecials] = useState([]);
    const [error, setError] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);
    const [qty, setQty] = useState(1);
    const [variantIdx, setVariantIdx] = useState(0);
    const { addToCart, loading: cartLoading, setOpen: setCartOpen } = useCart();

    useEffect(() => {
        let cancelled = false;
        getMenuProduct(storeSlug, productSlug)
            .then((d) => !cancelled && setData(d))
            .catch(
                (e) =>
                    !cancelled &&
                    setError(e?.response?.status === 404 ? "not found" : "error")
            );
        getLocation(storeSlug)
            .then((d) => !cancelled && setStore(d))
            .catch(() => {});
        getSpecials(storeSlug)
            .then((d) => !cancelled && setSpecials(d?.specials || []))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug, productSlug]);

    if (error === "not found") {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Product not found.
                </h1>
                <Link to={`/menu/${storeSlug}`} className="text-zen-olive underline">
                    Browse the menu
                </Link>
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                {error
                    ? "Live menu temporarily unavailable. Please try again."
                    : "Loading…"}
            </div>
        );
    }

    const { product, checkout_url } = data;
    const inStock = product.variants?.some((v) => v.quantity > 0);
    const variants = product.variants || [];
    const v0 = variants[variantIdx] || variants[0];
    const isOnSale = v0?.specialPriceRec && v0.specialPriceRec < v0.priceRec;
    const canonical = `https://weeddispensarynearme.com/menu/${storeSlug}/${productSlug}`;
    const images = product.images?.length ? product.images : [{ url: null }];

    // Match the variant to the first applicable Dutchie special (same logic
    // as ProductCard) so the buy box can render the deal link.
    const applicableSpecial = (() => {
        if (!v0) return null;
        const ids = [
            ...(v0.specialConditionIds || []),
            ...(v0.specialRewardIds || []),
        ];
        const byId = Object.fromEntries((specials || []).map((s) => [s.id, s]));
        for (const id of ids) {
            if (byId[id]) return byId[id];
        }
        return null;
    })();
    const sticker = discountSticker(v0, applicableSpecial);

    const handleAdd = async () => {
        if (!v0 || !inStock) return;
        try {
            await addToCart({
                slug: storeSlug,
                productId: product.id,
                option: v0.option || "",
                quantity: qty,
            });
        } catch (e) {
            // Cart add failed — usually upstream Dutchie inventory mismatch.
            // The cart context surfaces its own toast; this branch just keeps
            // the click handler from rejecting the promise to React.
            if (typeof console !== "undefined")
                console.warn("addToCart failed:", e);
        }
    };

    return (
        <>
            <SEOHead
                title={`${product.name} — ${product.brand?.name || product.category} | ZenZest`}
                description={(product.description || "").slice(0, 155)}
                canonical={canonical}
                image={images[0]?.url}
                type="product"
            />
            <SchemaJsonLd
                schema={[
                    productSchema(product, store, checkout_url),
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        {
                            name: store?.short_name || "Store",
                            url: `https://weeddispensarynearme.com/locations/${storeSlug}`,
                        },
                        {
                            name: "Menu",
                            url: `https://weeddispensarynearme.com/menu/${storeSlug}`,
                        },
                        { name: product.name, url: canonical },
                    ]),
                ]}
            />

            <section className="bg-zen-bg max-w-[1400px] mx-auto px-6 sm:px-10 pt-12 pb-24">
                <Link
                    to={`/menu/${storeSlug}`}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink mb-6"
                >
                    <ArrowLeft size={12} /> Back to menu
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-6">
                        <ProductGallery
                            images={images}
                            product={product}
                            imgIdx={imgIdx}
                            setImgIdx={setImgIdx}
                            inStock={inStock}
                            sticker={sticker}
                        />
                    </div>
                    <div className="lg:col-span-6">
                        <ProductBuyBox
                            product={product}
                            storeSlug={storeSlug}
                            store={store}
                            variants={variants}
                            variantIdx={variantIdx}
                            setVariantIdx={setVariantIdx}
                            v0={v0}
                            inStock={inStock}
                            isOnSale={isOnSale}
                            applicableSpecial={applicableSpecial}
                            qty={qty}
                            setQty={setQty}
                            onAdd={handleAdd}
                            cartLoading={cartLoading}
                            setCartOpen={setCartOpen}
                        />
                    </div>
                </div>
            </section>

            {/* Phase K — Product editorial (~500-700 words). Cache-first;
                first visit may have data.editorial=null (background gen). */}
            {data.editorial?.sections && (
                <section
                    data-testid="product-editorial"
                    className="bg-zen-bg border-y border-zen-line"
                >
                    <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                        {data.editorial.blurb && (
                            <p
                                data-testid="product-editorial-blurb"
                                className="font-serif text-lg sm:text-xl text-zen-ink leading-[1.6] mb-8 max-w-3xl"
                            >
                                {data.editorial.blurb}
                            </p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {[
                                ["what_it_is", "What it is"],
                                ["why_zenzest", "Why this matters at ZenZest"],
                                ["pairing", "Pairings"],
                                ["first_timer_notes", "If it's your first time"],
                            ]
                                .filter(([k]) => data.editorial.sections[k])
                                .map(([k, label]) => (
                                    <div
                                        key={k}
                                        data-testid={`product-editorial-${k}`}
                                        className="bg-white border border-zen-line p-5"
                                    >
                                        <h2 className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                            {label}
                                        </h2>
                                        <p className="text-[15px] text-zen-ink/90 leading-[1.75] whitespace-pre-line">
                                            {data.editorial.sections[k]}
                                        </p>
                                    </div>
                                ))}
                        </div>
                        {Array.isArray(data.editorial.faq) && data.editorial.faq.length > 0 && (
                            <div className="mt-10">
                                <h2 className="font-serif text-2xl text-zen-ink font-light mb-5">
                                    Frequently asked
                                </h2>
                                <div
                                    data-testid="product-editorial-faqs"
                                    className="border border-zen-line bg-white divide-y divide-zen-line"
                                >
                                    {data.editorial.faq.map((f, i) => (
                                        <details
                                            key={i}
                                            data-testid={`product-faq-${i}`}
                                            className="group px-5 py-4"
                                        >
                                            <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-serif text-base text-zen-ink leading-snug">
                                                <span>{f.q}</span>
                                                <span className="text-zen-olive text-xl leading-none group-open:rotate-45 transition-transform">
                                                    +
                                                </span>
                                            </summary>
                                            <p className="text-[14px] text-zen-ink/85 leading-[1.7] mt-3">
                                                {f.a}
                                            </p>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </>
    );
}
